import React, { useState } from 'react';
import {
  ILegalTransactionMeeting,
  legalTransactionMeetingSchema,
} from '../../Interfaces/ILegalTransactionMeeting';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { LocationOn, OpenInNew, Videocam } from '@mui/icons-material';
import { LegalTransactionMeetingEdit } from './LegalTransactionMeetingEdit';
import { ILegalTransactionMeetingEmail } from '../../Interfaces/ILegalTransactionMeetingEmail';
import { LegalTransactionMeetingRow } from './LegalTransactionMeetingRow';
import { castWithZeroTimezone } from '../../core/generic_helper';
import { LegalTransactionTalkRoomOverview } from './TalkRoom/LegalTransactionTalkRoomOverview';
import { ILegalTransactionTalkRoomDraft } from '../../Interfaces/ILegalTransactionTalkRoom';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { useGetConfig } from '../../hooks/data';
import { useGetAppArray } from '../../hooks/reactQuerys/AppQueries';
import { normalizeMeetingLegalClients } from './meetingLegalClient';
import { getLegalClientLabel } from './TalkRoom/talkRoomDisplay';

const formatMeetingDateTime = (dateValue: string) =>
  new Date(castWithZeroTimezone(dateValue)!).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDate = (dateValue: string) =>
  new Date(castWithZeroTimezone(dateValue)!).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatTime = (dateValue: string) =>
  new Date(castWithZeroTimezone(dateValue)!).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });

const getTillTime = (meeting: ILegalTransactionMeeting): string => {
  if (!meeting.Meeting_at || !meeting.DurationInMin) return '';
  const end = new Date(
    new Date(castWithZeroTimezone(meeting.Meeting_at)!).getTime() +
      meeting.DurationInMin * 60000
  );
  return end.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface IProps {
  idLegalTransaction: number;
  talkRooms: ILegalTransactionTalkRoomDraft[];
  setTalkRooms: React.Dispatch<
    React.SetStateAction<ILegalTransactionTalkRoomDraft[]>
  >;

  legalClients: ILegalClient[];
  idLegalTransactionType: number;
  meetingArray: ILegalTransactionMeeting[];
  setMeetingArray: (meetingArray: ILegalTransactionMeeting[]) => void;
  meetingEmailArray: ILegalTransactionMeetingEmail[];
  setMeetingEmailArray: (
    meetingEmailArray: ILegalTransactionMeetingEmail[]
  ) => void;
}

const getNewMeeting = (
  meetingArray: ILegalTransactionMeeting[],
  legalClients: ILegalClient[]
) => {
  let tmpId = -1;

  if (meetingArray.length > 0) {
    const tmpIdArray =
      Math.min(...meetingArray.map(x => x.idLegalTransactionMeeting)) - 1;

    if (tmpIdArray <= tmpId) {
      tmpId = tmpIdArray - 1;
    }
  }

  return {
    ...legalTransactionMeetingSchema.parse({
      idLegalTransactionMeeting: tmpId,
      idLegalTransaction: -1,
      LegalTransactionMeeting: '',
      Meeting_at: '',
      LegalClientArray: normalizeMeetingLegalClients(legalClients),
    }),
  } as ILegalTransactionMeeting;
};

export const LegalTransactionMeetingOverview: React.FC<IProps> = props => {
  const isAdmin = localStorage.getItem('role') === 'Administrator';

  const [addNew, setAddNew] = useState(false);
  const [selectedPastMeeting, setSelectedPastMeeting] =
    useState<ILegalTransactionMeeting | null>(null);
  const [newObject, setNewObject] = useState(
    getNewMeeting(props.meetingArray, props.legalClients)
  );
  const [newObjectEmailArray, setNewObjectEmailArray] = useState<
    ILegalTransactionMeetingEmail[]
  >([]);
  const { config } = useGetConfig();
  const { appArray } = useGetAppArray();
  const nextcloudActive =
    appArray?.find(a => a.system_key === 'nextcloud')?.IsEffective === true;

  const handleAddNew = () => {
    const localNewObject = getNewMeeting(
      props.meetingArray,
      props.legalClients
    );

    setNewObject({ ...localNewObject });
    setNewObjectEmailArray([]);
    setAddNew(true);
  };

  const handleSave = () => {
    props.setMeetingArray([...props.meetingArray, newObject]);
    props.setMeetingEmailArray([
      ...props.meetingEmailArray,
      ...newObjectEmailArray,
    ]);
    setAddNew(false);
  };

  return (
    <>
      <Dialog
        open={addNew}
        onClose={() => setAddNew(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>Neuer Termin</DialogTitle>
        <DialogContent dividers>
          <LegalTransactionMeetingEdit
            idLegalTransaction={props.idLegalTransaction}
            idLegalTransactionType={props.idLegalTransactionType}
            legalClients={props.legalClients}
            meetingObject={newObject}
            setMeetingObject={setNewObject}
            meetingEmailArray={newObjectEmailArray}
            setMeetingEmailArray={setNewObjectEmailArray}
            talkRooms={props.talkRooms}
            setTalkRooms={props.setTalkRooms}
            setAddNew={setAddNew}
            handleSave={handleSave}
          />
        </DialogContent>
      </Dialog>

      <Typography variant='h5'>
        Zukünftige Termine
        <Button
          variant='outlined'
          sx={{ float: 'right' }}
          onClick={handleAddNew}
        >
          Neuer Termin
        </Button>
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Datum</TableCell>
            <TableCell>Von</TableCell>
            <TableCell>Bis</TableCell>
            <TableCell>Terminbezeichnung</TableCell>
            <TableCell>Ort / Raum</TableCell>
            <TableCell>Mandanten</TableCell>
            <TableCell>(Geplante) E-Mails</TableCell>
            <TableCell>Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.meetingArray.filter(
            x => castWithZeroTimezone(x.Meeting_at) >= new Date()
          ).length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <i>Keine offene Termine.</i>
              </TableCell>
            </TableRow>
          ) : (
            props.meetingArray
              .filter(x => castWithZeroTimezone(x.Meeting_at) >= new Date())
              .sort((a, b) =>
                castWithZeroTimezone(a.Meeting_at) <
                castWithZeroTimezone(b.Meeting_at)
                  ? -1
                  : 1
              )
              .map(x => (
                <LegalTransactionMeetingRow
                  key={`idLegalTransactionMeeting-${x.idLegalTransactionMeeting}`}
                  idLegalTransaction={props.idLegalTransaction}
                  meetingObject={x}
                  meetingArray={props.meetingArray}
                  setMeetingArray={props.setMeetingArray}
                  legalClients={props.legalClients}
                  meetingEmailArray={props.meetingEmailArray}
                  setMeetingEmailArray={props.setMeetingEmailArray}
                  idLegalTransactionType={props.idLegalTransactionType}
                  talkRooms={props.talkRooms}
                  setTalkRooms={props.setTalkRooms}
                />
              ))
          )}
        </TableBody>
      </Table>

      {/* ── Detail-Dialog für zurückliegende Termine ───────────────────── */}
      {selectedPastMeeting && (
        <Dialog
          open
          onClose={() => setSelectedPastMeeting(null)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>
            {selectedPastMeeting.LegalTransactionMeeting}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Datum
                </Typography>
                <Typography>
                  {formatDate(selectedPastMeeting.Meeting_at)},{' '}
                  {formatTime(selectedPastMeeting.Meeting_at)}
                  {getTillTime(selectedPastMeeting) &&
                    ` – ${getTillTime(selectedPastMeeting)}`}{' '}
                  Uhr
                </Typography>
              </Box>

              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Ort / Raum
                </Typography>
                <Typography>
                  {selectedPastMeeting.TalkRoomObject?.RoomName ??
                    ([
                      selectedPastMeeting.LocationObject?.Location,
                      selectedPastMeeting.RoomObject?.Name,
                    ]
                      .filter(Boolean)
                      .join(' · ') ||
                      '–')}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Eingeladene Mandanten
                </Typography>
                {(selectedPastMeeting.LegalClientArray ?? []).length === 0 ? (
                  <Typography color='text.secondary'>–</Typography>
                ) : (
                  <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                    {(selectedPastMeeting.LegalClientArray ?? []).map(c => (
                      <Chip
                        key={c.idLegalClient}
                        label={getLegalClientLabel(c)}
                        size='small'
                        variant='outlined'
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Versendete E-Mails
                </Typography>
                <Typography>
                  {
                    props.meetingEmailArray.filter(
                      y =>
                        y.idLegalTransactionMeeting ===
                        selectedPastMeeting.idLegalTransactionMeeting
                    ).length
                  }
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>
      )}

      <Typography sx={{ mt: 5 }} variant='h6'>
        Zurückliegende Termine
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Datum</TableCell>
            <TableCell>Von</TableCell>
            <TableCell>Bis</TableCell>
            <TableCell>Bezeichnung</TableCell>
            <TableCell>Ort / Raum</TableCell>
            <TableCell>Mandanten</TableCell>
            <TableCell>(Versendete) E-Mails</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.meetingArray.filter(
            x => castWithZeroTimezone(x.Meeting_at) <= new Date()
          ).length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>
                <i>Keine zurückliegende Termine vorhanden.</i>
              </TableCell>
            </TableRow>
          ) : (
            props.meetingArray
              .filter(x => castWithZeroTimezone(x.Meeting_at) <= new Date())
              .sort((a, b) =>
                castWithZeroTimezone(a.Meeting_at) <
                castWithZeroTimezone(b.Meeting_at)
                  ? -1
                  : 1
              )
              .map(x => {
                const clients = x.LegalClientArray ?? [];
                const maxVisible = 2;
                const visible = clients.slice(0, maxVisible);
                const overflow = clients.length - maxVisible;
                return (
                  <TableRow
                    key={`past-meeting-${x.idLegalTransactionMeeting}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedPastMeeting(x)}
                  >
                    <TableCell>{formatDate(x.Meeting_at)}</TableCell>
                    <TableCell>{formatTime(x.Meeting_at)}</TableCell>
                    <TableCell>{getTillTime(x)}</TableCell>
                    <TableCell>{x.LegalTransactionMeeting}</TableCell>
                    <TableCell>
                      {x.idLegalTransactionTalkRoom ? (
                        <Stack
                          direction='row'
                          spacing={0.5}
                          alignItems='center'
                        >
                          <Chip
                            icon={<Videocam />}
                            label={x.TalkRoomObject?.RoomName ?? 'Online'}
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                          {x.TalkRoomObject?.JoinUrl && (
                            <Tooltip title='Meeting beitreten'>
                              <IconButton
                                size='small'
                                color='primary'
                                component='a'
                                href={x.TalkRoomObject.JoinUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={e => e.stopPropagation()}
                              >
                                <OpenInNew fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : x.idLocation ? (
                        <Chip
                          icon={<LocationOn />}
                          label={[
                            x.LocationObject?.Location ?? 'Standort',
                            x.RoomObject?.Name,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                          size='small'
                          variant='outlined'
                        />
                      ) : (
                        <span style={{ color: '#aaa' }}>–</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                        {visible.map(c => (
                          <Chip
                            key={c.idLegalClient}
                            label={getLegalClientLabel(c)}
                            size='small'
                            variant='outlined'
                          />
                        ))}
                        {overflow > 0 && (
                          <Tooltip
                            title={clients
                              .slice(maxVisible)
                              .map(c => getLegalClientLabel(c))
                              .join(', ')}
                          >
                            <Chip label={`+${overflow}`} size='small' />
                          </Tooltip>
                        )}
                        {clients.length === 0 && (
                          <span style={{ color: '#aaa', fontSize: '0.75rem' }}>
                            –
                          </span>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {
                        props.meetingEmailArray.filter(
                          y =>
                            y.idLegalTransactionMeeting ===
                            x.idLegalTransactionMeeting
                        ).length
                      }
                    </TableCell>
                  </TableRow>
                );
              })
          )}
        </TableBody>
      </Table>
      {isAdmin && config?.App.includes('nextcloud') && nextcloudActive && (
        <LegalTransactionTalkRoomOverview
          idLegalTransaction={props.idLegalTransaction}
          talkRooms={props.talkRooms}
          onChangeTalkRooms={props.setTalkRooms}
          legalClients={props.legalClients}
          meetingArray={props.meetingArray}
        />
      )}
    </>
  );
};
